import { Loading } from '@components/atomic/atoms';
import DebouncedSearchInput from '@components/atomic/molecules/DebouncedSearchInput/DebouncedSearchInput';
import CustomPaginationMui from '@components/materialComponents/CustomPaginationMui';
import { getEntityList, getPaymentsByInfo } from '@lib/api';
import { useQuery } from '@tanstack/react-query';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { Fragment, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';

interface EntitiesTableProps {
	entityType: string;
	projectId: string;
	subActiveKey: string;
	localeText: any;
	columnDefs: ColDef[];
	defaultColDef: ColDef;
	gridRef: React.RefObject<AgGridReact>;
	onGridReady: (params: any, gridRef: React.RefObject<AgGridReact>) => void;
	sbShow: React.ReactNode;
	batchState: string;
}

const EntitiesTable = ({
	entityType,
	projectId,
	subActiveKey,
	localeText,
	columnDefs,
	defaultColDef,
	gridRef,
	onGridReady,
	sbShow,
	batchState,
}: EntitiesTableProps) => {
	const [entities, setEntities] = useState<any[]>();
	const [entitiesMeta, setEntitiesMeta] = useState<any>({});
	const [term, setTerm] = useState('');

	const entitiesQuery = useQuery({
		queryKey: ['entities', entityType, projectId, term],
		queryFn: async () => {
			if (entityType === 'payments') {
				return getPaymentsByInfo(projectId, entityType, term);
			}
			return getEntityList(entityType, projectId, entityType, term);
		},
		enabled: subActiveKey === 'entities' && !!projectId,
		select: (resp) => {
			if (!resp.success) return { data: [], meta: {} };
			const { data, success, ...meta } = resp;
			return { data, meta };
		},
		refetchInterval: batchState === 'DOING' ? 5000 : false,
	});

	useEffect(() => {
		if (entitiesQuery.data) {
			setEntities(entitiesQuery.data.data);
			setEntitiesMeta(entitiesQuery.data.meta);
		}
	}, [entitiesQuery.data, setEntities, setEntitiesMeta]);

	return (
		<Fragment>
			<Row className="align-items-center mb-4">
				<Col className="col-4">
					<DebouncedSearchInput
						onSearchTermChange={(term) => {
							setTerm(term);
						}}
					/>
				</Col>
			</Row>

			{sbShow}

			<Row>
				<Col className="col-12 col-xxl-9" id="grid-wrapper">
					{Array.isArray(entities) ? (
						<div className="ag-theme-alpine list-table">
							<AgGridReact
								ref={gridRef}
								columnDefs={columnDefs}
								rowData={entities}
								defaultColDef={defaultColDef}
								domLayout="autoHeight"
								tooltipShowDelay={0}
								tooltipHideDelay={2000}
								tooltipMouseTrack={true}
								onGridReady={(e) => onGridReady(e, gridRef)}
								headerHeight={50}
								rowHeight={50}
								suppressCellFocus={true}
								animateRows={true}
								localeText={localeText}
							/>
							{Object.keys(entitiesMeta).length > 0 && (
								<CustomPaginationMui
									dataMeta={entitiesMeta}
									setData={setEntities}
									setDataMeta={setEntitiesMeta}
									refTable={entityType}
									setIsFirstResearch={() => {}}
									setIsChangedPagination={() => {}}
								/>
							)}
						</div>
					) : (
						<Loading />
					)}
				</Col>
			</Row>
		</Fragment>
	);
};

export default EntitiesTable;
